import { Box, Typography, InputLabel } from "@mui/material";
import FeedbackIcon from "@mui/icons-material/Feedback";
import { CSSProperties, useEffect, useState } from "react";
import { getJobNotes, updateJobNotes } from "../../api/batches";
import { useRef, useCallback } from "react";
import { useNotification } from "@refinedev/core";
import { useAuthStore } from "../../authProvider";

// Enum for save states
type SaveState = "saved" | "saving" | "to_save";

export default function EditorNotes({ jobId }: { jobId: string }) {
  const { user } = useAuthStore();

  const { open } = useNotification();
  const [notes, setNotes] = useState<string>("");
  const [clientNotes, setClientNotes] = useState<string>("");
  const [userFeedback, setUserFeedback] = useState<string>("");

  const [notesOriginallyFilled, setNotesOriginallyFilled] = useState(false);
  const [clientNotesOriginallyFilled, setClientNotesOriginallyFilled] =
    useState(false);
  const [notesHaveBeenFilled, setNotesHaveBeenFilled] = useState(false);
  const [clientNotesHaveBeenFilled, setClientNotesHaveBeenFilled] =
    useState(false);
  const [notesOriginal, setNotesOriginal] = useState<string>("");
  const [clientNotesOriginal, setClientNotesOriginal] = useState<string>("");
  const [notesSaveState, setNotesSaveState] = useState<SaveState>("saved");
  const [clientNotesSaveState, setClientNotesSaveState] =
    useState<SaveState>("saved");

  const [textareaHeight, setTextareaHeight] = useState<number>(80);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const clientTextareaRef = useRef<HTMLTextAreaElement>(null);

  const saveTimeout = useRef<NodeJS.Timeout | null>(null);
  const clientSaveTimeout = useRef<NodeJS.Timeout | null>(null);

  // Function to calculate and synchronize the height of both textareas
  const adjustHeight = useCallback(() => {
    const internalTextarea = textareaRef.current;
    const clientTextarea = clientTextareaRef.current;
    const maxHeight = 150;

    if (internalTextarea && clientTextarea) {
      // Reset heights to calculate the proper scrollHeight
      internalTextarea.style.height = "auto";
      clientTextarea.style.height = "auto";

      // Calculate required heights based on content
      const internalScrollHeight = Math.max(80, internalTextarea.scrollHeight);
      const clientScrollHeight = Math.max(80, clientTextarea.scrollHeight);

      // Use the maximum height between the two textareas (but not exceeding maxHeight)
      const commonHeight = Math.min(
        maxHeight,
        Math.max(internalScrollHeight, clientScrollHeight)
      );

      // Update state with the synchronized height
      setTextareaHeight(commonHeight);

      // Apply the same height to both textareas
      internalTextarea.style.height = `${commonHeight}px`;
      clientTextarea.style.height = `${commonHeight}px`;

      // Enable scrolling if content exceeds the height
      internalTextarea.style.overflowY =
        internalTextarea.scrollHeight > commonHeight ? "auto" : "hidden";
      clientTextarea.style.overflowY =
        clientTextarea.scrollHeight > commonHeight ? "auto" : "hidden";
    }
  }, []);

  // Adjust heights whenever notes or client notes change
  useEffect(() => {
    adjustHeight();
  }, [notes, clientNotes, adjustHeight]);

  useEffect(() => {
    getJobNotes({ jobId }).then((response) => {
      const fetchedNotes = response.data.data || "";

      setNotes(fetchedNotes.notes || "");
      setClientNotes(fetchedNotes.client_notes || "");
      setUserFeedback(fetchedNotes.user_feedback || "");

      setNotesOriginal(fetchedNotes.notes || "");
      setNotesOriginallyFilled(fetchedNotes.notes !== "");
      setNotesHaveBeenFilled(fetchedNotes.notes !== "");
      setClientNotesOriginal(fetchedNotes.client_notes || "");
      setClientNotesOriginallyFilled(fetchedNotes.client_notes !== "");
      setClientNotesHaveBeenFilled(fetchedNotes.client_notes !== "");
    });
  }, [jobId]);

  // Update save state when notes change
  useEffect(() => {
    if (notes !== notesOriginal) {
      setNotesSaveState("to_save");
    } else {
      setNotesSaveState("saved");
    }
  }, [notes, notesOriginal]);

  // Update client save state when client notes change
  useEffect(() => {
    if (clientNotes !== clientNotesOriginal) {
      setClientNotesSaveState("to_save");
    } else {
      setClientNotesSaveState("saved");
    }
  }, [clientNotes, clientNotesOriginal]);

  // Helper function to handle saving notes
  const handleSaveNotes = (value: string, isClientNotes: boolean = false) => {
    // Set state to "saving" when starting the save operation
    if (isClientNotes) {
      setClientNotesSaveState("saving");
    } else {
      setNotesSaveState("saving");
    }

    updateJobNotes({
      jobId,
      notes: isClientNotes ? notes : value,
      client_notes: isClientNotes ? value : clientNotes,
    })
      .then(() => {
        // Update original notes and set state to "saved"
        if (isClientNotes) {
          setClientNotesOriginal(value);
        } else {
          setNotesOriginal(value);
        }

        setClientNotesSaveState("saved");
        setNotesSaveState("saved");
      })
      .catch(() => {
        open?.({
          message: `Errore durante il salvataggio delle ${
            isClientNotes ? "note cliente" : "note interne"
          }.`,
          type: "error",
        });
      });
  };

  const labelStyle: CSSProperties = {
    position: "absolute",
    top: "1px",
    left: "2px",
    right: "2px",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "0.75rem",
    fontWeight: "bold",
    zIndex: 1,
    background: "white",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };

  const textareaStyle: CSSProperties = {
    width: "100%",
    borderRadius: "2px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    fontFamily: "inherit",
    fontSize: "0.90rem",
    transition: "border-color 0.2s ease-in-out",
    boxSizing: "border-box",
    resize: "none",
    overflow: "auto",
    height: `${textareaHeight}px`, // Use the synchronized height from state
    outline: "none",
    paddingTop: "32px",
    paddingLeft: "10px",
    paddingRight: "10px",
    paddingBottom: "10px",
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexDirection: "row" }}>
        <Box sx={{ flex: 1, position: "relative" }}>
          <InputLabel htmlFor="internal-notes" sx={labelStyle}>
            Internal notes
            {(notes !== "" || notesHaveBeenFilled || notesOriginallyFilled) && (
              <Typography
                sx={{
                  position: "absolute",
                  top: "2px",
                  right: "8px",
                  fontSize: "0.75rem",
                  color: notesSaveState === "saved" ? "green" : "red",
                }}
              >
                {notesSaveState === "saved" ? "Saved" : "Saving..."}
              </Typography>
            )}
          </InputLabel>
          <textarea
            ref={textareaRef}
            id="internal-notes"
            value={notes || ""}
            onChange={(e) => {
              setNotes(e.target.value);
              setNotesHaveBeenFilled(true);

              if (saveTimeout.current) {
                clearTimeout(saveTimeout.current);
              }

              // Set state to "to_save" immediately when typing
              setNotesSaveState("to_save");

              saveTimeout.current = setTimeout(() => {
                handleSaveNotes(e.target.value);
              }, 500);

              // Ensure heights are adjusted after typing
              setTimeout(adjustHeight, 0);
            }}
            placeholder="Internal notes"
            style={textareaStyle}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            position: "relative",
            display: user?.isClientRelated ? "none" : "block",
          }}
        >
          <InputLabel htmlFor="client-notes" sx={labelStyle}>
            Note cliente
            {(clientNotes !== "" ||
              clientNotesHaveBeenFilled ||
              clientNotesOriginallyFilled) && (
              <Typography
                sx={{
                  position: "absolute",
                  top: "2px",
                  right: "8px",
                  fontSize: "0.75rem",
                  color: clientNotesSaveState === "saved" ? "green" : "red",
                }}
              >
                {clientNotesSaveState === "saved" ? "Saved" : "Saving..."}
              </Typography>
            )}
          </InputLabel>
          <textarea
            ref={clientTextareaRef}
            id="client-notes"
            value={clientNotes || ""}
            onChange={(e) => {
              setClientNotes(e.target.value);

              if (clientSaveTimeout.current) {
                clearTimeout(clientSaveTimeout.current);
              }

              // Set state to "to_save" immediately when typing
              setClientNotesSaveState("to_save");

              clientSaveTimeout.current = setTimeout(() => {
                handleSaveNotes(e.target.value, true);
              }, 500);

              // Ensure heights are adjusted after typing
              setTimeout(adjustHeight, 0);
            }}
            placeholder="Notes for the client"
            style={textareaStyle}
          />
        </Box>
      </Box>

      {userFeedback && userFeedback !== "" && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="subtitle2"
            component="div"
            sx={{
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              color: "#555",
            }}
          >
            <FeedbackIcon
              sx={{ mr: 0.5, fontSize: "1rem", color: "primary.main" }}
            />
            Client Feedback
          </Typography>
          <textarea
            readOnly
            value={userFeedback || ""}
            style={{
              marginTop: "0.5rem",
              padding: "0.5rem",
              width: "100%",
              borderRadius: "4px",
              border: "1px solid rgba(0, 0, 0, 0.23)",
              fontFamily: "inherit",
              fontSize: "1rem",
              boxSizing: "border-box",
              resize: "none",
              overflow: "auto", // Allow scrolling
              height: "80px", // Fixed height
              backgroundColor: "#f9f9f9", // Slightly different background
              outline: "none",
              borderLeft: "3px solid #3f51b5", // Distinctive left border
            }}
          />
        </Box>
      )}
    </Box>
  );
}
